/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs'
import * as Yup from 'yup'

import {
  COUNTRIES,
  EVENT_SHIRT_OPTIONS,
  GENDERS,
  SHIRT_KEY,
  TSHIRT_SIZES,
} from '../../../constants/models'

const validateShirtType = (key: string, value: any, testContext: any) => {
  if (
    key === SHIRT_KEY.SHIRT1 ||
    (key === SHIRT_KEY.SHIRT2 && testContext.parent.hasShirt2)
  ) {
    if (
      testContext.parent.shirtDetails &&
      'TYPE_OPTIONS' in testContext.parent.shirtDetails[key]
    ) {
      if (value !== undefined && value !== null) {
        const shirtOptions =
          testContext.parent.shirtDetails[key]?.TYPE_OPTIONS ||
          EVENT_SHIRT_OPTIONS

        if (!shirtOptions.includes(value)) {
          return testContext.createError({
            message: `Invalid ${
              testContext.parent.shirtDetails[key].LABEL || 'Shirt'
            } type selected`,
            path: testContext.path, // Fieldname
          })
        }
        return true
      }
      return testContext.createError({
        message: `${
          testContext.parent.shirtDetails[key].LABEL || 'Shirt'
        } type is required`,
        path: testContext.path, // Fieldname
      })
    }
  }
  return true
}

const validateShirtSize = (key: string, value: any, testContext: any) => {
  if (
    key === SHIRT_KEY.SHIRT1 ||
    (key === SHIRT_KEY.SHIRT2 && testContext.parent.hasShirt2)
  ) {
    if (value !== undefined && value !== null) {
      const options =
        testContext.parent.shirtDetails[key]?.SIZE_OPTIONS ||
        Object.keys(TSHIRT_SIZES)

      if (!options.includes(value)) {
        return testContext.createError({
          message: `Invalid ${
            testContext.parent.shirtDetails[key].LABEL || 'Shirt'
          } size selected`,
          path: testContext.path, // Fieldname
        })
      }
      return true
    }
    return testContext.createError({
      message: `${
        testContext.parent.shirtDetails[key].LABEL || 'Shirt'
      } size is required`,
      path: testContext.path, // Fieldname
    })
  }
  return true
}

export const validateAgeRange = (
  value: any,
  testContext: any,
  field: string = 'ageRange',
) => {
  if (value !== undefined && value !== null) {
    if (
      field in testContext.parent &&
      'eventEndDateTime' in testContext.parent &&
      testContext.parent[field]?.length > 0
    ) {
      const bday = dayjs(`${value}T23:59:59+08:00`, 'YYYY-MM-DDTHH:mm:ssZ')
      const eventDay = dayjs(testContext.parent.eventEndDateTime)
      const range = testContext.parent[field].split('-')
      const ageDuringEvent = eventDay.diff(bday, 'year')
      const ageStart = isNaN(parseInt(range[0])) ? 0 : parseInt(range[0])
      const ageEnd = isNaN(parseInt(range[1])) ? 100 : parseInt(range[1])
      if (ageDuringEvent < ageStart || ageDuringEvent > ageEnd) {
        let message = ''
        if (field === 'tandemAgeRange') {
          message = `Tandem participant must be between ages ${testContext.parent[field]}`
        } else {
          if (ageStart === 0 && ageEnd > 0) {
            message = `Participant must be ${ageEnd} yrs old or below`
          } else if (ageStart > 0 && ageEnd >= 100) {
            message = `Participant must be ${ageStart} yrs old or above`
          } else {
            message = `Participant must be between ages ${testContext.parent[field]}`
          }
        }
        return testContext.createError({
          message,
          path: testContext.path, // Fieldname
        })
      }
    }
  }
  return true
}

export const commonSchema = {
  categoryId: Yup.string().required('Category ID is required'),
  firstName: Yup.string()
    .max(50, 'First Name must not exceed 50 characters')
    .required('First Name is required'),
  lastName: Yup.string()
    .max(50, 'Last Name must not exceed 50 characters')
    .required('Last Name is required'),
  teamName: Yup.string().max(50, 'Team name must not exceed 50 characters'),
  bibName: Yup.string()
    //   .required('Name on bib is required')
    .max(25, 'Name on bib must not exceed 25 numbers'),
  shirtSize: Yup.mixed().test({
    name: 'required-with-hasShirt',
    test: (value: any, testContext: any) =>
      validateShirtSize(SHIRT_KEY.SHIRT1, value, testContext),
  }),
  shirtType: Yup.string().test({
    name: 'required-with-hasShirtType',
    test: (value: any, testContext: any) =>
      validateShirtType(SHIRT_KEY.SHIRT1, value, testContext),
  }),
  eventEndDateTime: Yup.mixed(),
  birthdate: Yup.mixed()
    .required('Birthdate is required')
    .test(
      'validate-format',
      'Birthdate must be valid date in format YYYY-MM-DD',
      (value) => {
        return dayjs(value as string, 'YYYY-MM-DD', true).isValid()
      },
    )
    .test('validate-future', 'Birthdate must not be a future date', (value) => {
      if (value !== undefined) return dayjs().isAfter(value as Date)
    })
    .test({
      name: 'validate-range',
      test: (value: any, testContext: any) =>
        validateAgeRange(value, testContext),
    }),
  nationality: Yup.mixed()
    .required('Nationality is required')
    .oneOf(Object.keys(COUNTRIES)),
  gender: Yup.mixed()
    .required('Gender is required')
    .oneOf(Object.keys(GENDERS)),
  address: Yup.string()
    .max(100, 'Address must not exceed 100 characters')
    .required('Address is required'),
  phoneNumber: Yup.string()
    .max(20, 'Phone must not exceed 20 characters')
    .required('Phone is required'),
  // confirmPhoneNumber: Yup.string()
  //   .max(20, 'Confirm Phone must not exceed 20 characters')
  //   .required('Confirm Phone is required')
  //   .oneOf([Yup.ref('phoneNumber'), null], 'Phone numbers must match'),
  // confirmEmail: Yup.string().test(
  //   'not-equal-email',
  //   'Email addresses must match',
  //   (value, testContext) => {
  //     if (
  //       'email' in testContext.parent &&
  //       testContext.parent.email.length > 0
  //     ) {
  //       return testContext.parent.email === value
  //     }
  //     return true
  //   },
  // ),
  emergencyContactName: Yup.string()
    .max(50, 'Emergency Contact Name must not exceed 50 characters')
    .required('Emergency Contact Name is required'),
  emergencyContactPhone: Yup.string()
    .max(20, 'Emergency Contact Number must not exceed 20 characters')
    .required('Emergency Contact Number is required')
    .test(
      'not-equal-phoneNumber',
      'Emergency Contact Number must not be equal to Phone number',
      (value, testContext) => {
        if (
          'phoneNumber' in testContext.parent &&
          testContext.parent.phoneNumber.length > 0
        ) {
          return testContext.parent.phoneNumber !== value
        }
        return false
      },
    ),
  // bike jersey
  hasJersey: Yup.boolean(),
  // 2nd shirt
  hasShirt2: Yup.boolean(),
  shirtSize2: Yup.mixed().test({
    name: 'required-with-hasShirt2',
    test: (value: any, testContext: any) =>
      validateShirtSize(SHIRT_KEY.SHIRT2, value, testContext),
  }),
  shirtType2: Yup.string().test({
    name: 'required-with-hasShirtType2',
    test: (value: any, testContext: any) =>
      validateShirtType(SHIRT_KEY.SHIRT2, value, testContext),
  }),
}
